/**
 * MarkDataPlateButton — "This is a data plate" on a gallery photo nobody tagged (ISS-612, Nick 2026-10-24:
 * "I need 1-click access to the version of the photos where I can connect the photos to the data plate").
 *
 * One click tags the photo; the viewer then shows "This plate is for:" for the same photo, so the person is one
 * more click from tying it to a component while the plate stays big on screen. Shown only on a gallery photo that is
 * not already a plate, not removed, and carrying no label a person chose (the server refuses that case too, as the authority
 * for two people acting at once). The button stays mounted while saving, so "Saving..." is seen.
 * Offline it is disabled with a plain message: a queued write has conflict rules nobody has ruled yet.
 */
import { ScanLine } from 'lucide-react';
import { useIsOffline } from '../../../hooks/useIsOffline';
import { useMarkDataPlate } from '../../../hooks/fieldService/useMarkDataPlate';
import { isPlatePhoto } from '../../../lib/fieldService/plateComponents';
import type { GeneratorPhoto } from '../../../types/fieldService/generatorPhotos';

interface MarkDataPlateButtonProps {
  photo: GeneratorPhoto;
  generatorDocId: string;
}

/** Hooks live in the inner component, so a photo that is already a plate costs no mutation setup. */
export function MarkDataPlateButton({ photo, generatorDocId }: MarkDataPlateButtonProps) {
  const personLabelled = typeof photo.category === 'string' && photo.category !== 'unknown';
  return photo.state === 'gallery' && !isPlatePhoto(photo) && !personLabelled ? <MarkButton key={photo.id} photo={photo} generatorDocId={generatorDocId} /> : null;
}

function MarkButton({ photo, generatorDocId }: MarkDataPlateButtonProps) {
  const mark = useMarkDataPlate(generatorDocId);
  const offline = useIsOffline();
  return (
    <button
      type="button"
      onClick={() => mark.mutate({ photoId: photo.id })}
      disabled={offline || mark.isPending}
      title={offline ? 'You are offline: saving needs a connection' : 'Tag this photo as a data plate'}
      className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full bg-white/10 px-3 text-sm text-white transition-colors hover:bg-white/20 disabled:opacity-50"
    >
      <ScanLine className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{mark.isPending ? 'Saving...' : 'This is a data plate'}</span>
    </button>
  );
}
